import { Sprite, Texture } from "pixi.js";
import type { Application } from "pixi.js";

import type { StingerConfig } from "./SceneTransition";

interface CachedStinger {
  video: HTMLVideoElement;
  objectUrl: string | null;
  // Nodos WebAudio para rutear el audio del stinger al mix del broadcast.
  // createMediaElementSource sólo puede llamarse 1× por elemento → se cachean.
  gain?: GainNode;
}

/**
 * Reproduce un "stinger": una animación branded full-screen (WebM con alpha) que
 * tapa el corte de escena. El swap real ocurre en el cut-point (cuando el video
 * cubre toda la pantalla), oculto bajo la animación. NO es un tween — es un
 * overlay con timing. Precarga + cachea el <video> por asset para que el 1er pase
 * no tenga hipo, y reusa la textura entre pases.
 */
export default class StingerPlayer {
  private App: Application;
  private W: number;
  private H: number;
  private cache = new Map<string, CachedStinger>();
  // Completa el stinger en curso al instante (swap + cleanup), igual patrón que
  // el TransitionEngine: interrumpir un pase no debe colgar ni dejar el sprite.
  private finishCurrent: (() => void) | null = null;
  // AudioContext + destino del mix del broadcast (GlobalAudio) para rutear el
  // audio del stinger al stream. Si no están, el sonido cae a video.muted local.
  private audioCtx?: AudioContext;
  private audioDest?: AudioNode;

  constructor(
    app: Application,
    width: number,
    height: number,
    audioCtx?: AudioContext,
    audioDest?: AudioNode,
  ) {
    this.App = app;
    this.W = width;
    this.H = height;
    this.audioCtx = audioCtx;
    this.audioDest = audioDest;
  }

  /**
   * Rutea el audio del stinger al mix del broadcast vía WebAudio y devuelve el
   * GainNode para prender/apagar el sonido. createMediaElementSource es 1× por
   * elemento → se cachea el grafo. Devuelve null si no hay AudioContext (cae a
   * video.muted local).
   */
  private ensureAudio(entry: CachedStinger): GainNode | null {
    if (!this.audioCtx || !this.audioDest) return null;
    if (entry.gain) return entry.gain;
    try {
      const src = this.audioCtx.createMediaElementSource(entry.video);
      const gain = this.audioCtx.createGain();
      gain.gain.value = 0;
      src.connect(gain);
      gain.connect(this.audioDest);
      entry.gain = gain;
      return gain;
    } catch (e) {
      console.warn("[StingerPlayer] audio routing error:", e);
      return null;
    }
  }

  private keyOf(cfg: StingerConfig): string {
    return `${cfg.directory}${cfg.fileName}`;
  }

  private async resolveUrl(cfg: StingerConfig): Promise<{
    url: string;
    objectUrl: string | null;
  }> {
    const protocolUrl = `eriol-workspace:/${cfg.directory}${cfg.fileName}`;
    try {
      const res = await fetch(protocolUrl);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      return { url: objectUrl, objectUrl };
    } catch {
      return { url: protocolUrl, objectUrl: null };
    }
  }

  /** Precarga (idempotente) el <video> del stinger. Llamar al setear la config. */
  async preload(cfg: StingerConfig): Promise<HTMLVideoElement | null> {
    const key = this.keyOf(cfg);
    const existing = this.cache.get(key);
    if (existing) return existing.video;

    try {
      const { url, objectUrl } = await this.resolveUrl(cfg);
      const video = document.createElement("video");
      video.src = url;
      video.preload = "auto";
      video.muted = true; // v1: sin audio en el mix (ver plan F4-audio)
      video.loop = false;
      video.playsInline = true;
      video.crossOrigin = "anonymous";
      video.load();
      this.cache.set(key, { video, objectUrl });
      return video;
    } catch (e) {
      console.warn("[StingerPlayer] preload error:", e);
      return null;
    }
  }

  /** Completa el stinger en curso al instante (interrupción / teardown). */
  cancel(): void {
    if (this.finishCurrent) this.finishCurrent();
  }

  /**
   * Corre el stinger. Mantiene `fromRoot` visible; en el cut-point dispara
   * `onSwap()` (el compositor muestra el entrante + oculta/pausa el saliente);
   * al terminar el video limpia el sprite y resuelve. Si el asset falla, hace el
   * swap igual (degrada a corte) para no romper el pase.
   */
  async run(cfg: StingerConfig, onSwap: () => void): Promise<void> {
    this.cancel(); // completa cualquier stinger anterior

    const video = await this.preload(cfg);

    if (!video) {
      onSwap(); // sin asset → corte directo
      return;
    }

    // Sonido: ruteado al mix del broadcast (gain on/off). Sin AudioContext cae a
    // video.muted local (el operador igual lo escucha por la salida default).
    const entry = this.cache.get(this.keyOf(cfg));
    const gain = entry ? this.ensureAudio(entry) : null;
    if (gain) {
      gain.gain.value = cfg.sound ? 1 : 0;
      video.muted = false;
    } else {
      video.muted = !cfg.sound;
    }

    const texture = Texture.from(video);
    const sprite = new Sprite(texture);
    sprite.label = "stinger";
    sprite.width = this.W;
    sprite.height = this.H;
    sprite.x = 0;
    sprite.y = 0;
    // Por encima de los dos Roots de escena (que usan zIndex 0/1 en transiciones).
    sprite.zIndex = 1_000_000;
    // Gotcha alpha: si el WebM-alpha se ve con halo/borde, ajustar
    // texture.source.alphaMode acá (validar en runtime).
    this.App.stage.addChild(sprite);

    try {
      video.currentTime = (cfg.startMs ?? 0) / 1000;
      await video.play();
    } catch {
      /* autoplay puede rechazar; el ticker igual chequea currentTime/ended */
    }

    return new Promise<void>((resolve) => {
      let done = false;
      let swapped = false;

      const doSwap = () => {
        if (swapped) return;
        swapped = true;
        onSwap();
      };

      const cleanup = () => {
        if (done) return;
        done = true;
        this.App.ticker.remove(tick);
        this.finishCurrent = null;
        video.onended = null;
        doSwap(); // garantiza el swap aunque se interrumpa antes del cut-point
        try {
          video.pause();
        } catch {
          /* noop */
        }
        if (!sprite.destroyed) {
          sprite.removeFromParent();
          sprite.destroy({ texture: false }); // la textura/video quedan cacheados
        }
        resolve();
      };

      const tick = () => {
        const t = video.currentTime * 1000;
        if (video.ended || (cfg.endMs ? t >= cfg.endMs : false)) {
          cleanup();
          return;
        }
        if (!swapped && t >= cfg.cutPointMs) doSwap();
      };

      this.finishCurrent = cleanup;
      video.onended = cleanup;
      this.App.ticker.add(tick);
    });
  }

  /** Libera videos cacheados + object URLs + nodos de audio (teardown). */
  destroy(): void {
    this.cancel();
    for (const { video, objectUrl, gain } of this.cache.values()) {
      try {
        gain?.disconnect();
      } catch {
        /* noop */
      }
      try {
        video.pause();
        video.removeAttribute("src");
        video.load();
      } catch {
        /* noop */
      }
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
    this.cache.clear();
  }
}
